import { enableMapSet } from 'immer';
import { mountStoreDevtool } from 'simple-zustand-devtools';
import { create } from 'zustand';
import type { StateCreator } from 'zustand/index';
import { persist, subscribeWithSelector } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { Catalog } from '../Models/Catalog';
import type { Filters } from '../Models/Filters';

enableMapSet();

type CatalogSlice = {
  catalog?: Catalog;
  clearStorage: () => void;
  setCatalog: (catalog: Catalog) => void;
};

type LeftFiltersSlice = {
  leftFilters: Filters;
  isApplyFiltersShown: boolean;
  setIsApplyFiltersShown: (isShown: boolean) => void;
  setLeftFilters: (leftFilters: Filters) => void;
};

export const LOCAL_STORAGE_STORE_KEY = 'psw-store';

const createCatalogSlice: StateCreator<
  CatalogSlice & LeftFiltersSlice,
  [['zustand/immer', never]],
  [],
  CatalogSlice
> = (set, get) => ({
  catalog: undefined,
  setCatalog: catalog => set({ catalog }),
  clearStorage: () => localStorage.removeItem('psw-store'),
});

const createLeftFiltersSlice: StateCreator<
  LeftFiltersSlice & CatalogSlice,
  [['zustand/immer', never]],
  [],
  LeftFiltersSlice
> = (set, get) => ({
  leftFilters: {} as unknown as Filters,
  isApplyFiltersShown: false,
  setIsApplyFiltersShown: isShown =>
    set(state => {
      state.isApplyFiltersShown = isShown;
    }),
  setLeftFilters: leftFilters =>
    set(state => {
      state.leftFilters = leftFilters;
    }),
});

export const useStore = create<LeftFiltersSlice & CatalogSlice>()(
  subscribeWithSelector(
    persist(
      immer((setState, getState, store) => ({
        ...createLeftFiltersSlice(setState, getState, store),
        ...createCatalogSlice(setState, getState, store),
      })),
      {
        name: LOCAL_STORAGE_STORE_KEY,
        partialize: state => ({ catalog: state.catalog }),
        merge: (persistedState, currentState) => {
          return {
            ...currentState,
            catalog: new Catalog((persistedState as { catalog: ConstructorParameters<typeof Catalog>[0] }).catalog),
          };
        },
        version: 4,
      },
    ),
  ),
);

if (process.env.NODE_ENV === 'dev-local' || process.env.NODE_ENV === 'development') {
  mountStoreDevtool('Store', useStore);
}
